import React, { useState, useEffect, useCallback } from 'react';
import { Redirect } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Table,
  Label,
  FormGroup,
  Button,
  Badge,
  Input,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import classnames from 'classnames';
import { connect } from 'react-redux';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import PageDataLoader from '../../Utility/PageDataLoader';
import Paginator from '../../Utility/Paginator';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import {
  sendWhatsAppMessage,
  fetchWhatsAppCommunications,
  getWhatsAppCommunicationById,
  getWhatsAppTemplates,
  createWhatsAppTemplate,
  fetchWhatsAppStats
} from '../../../api/whatsapp';
import { getChildList } from '../../../api/child';
import { getBatchList } from '../../../api/batch';
import { getOfferingList } from '../../../api/offering';
import moment from 'moment';
import { validatePermissionKey } from '../../../helpers/permission';
import {
  SEND_WHATSAPP_MESSAGE,
  VIEW_WHATSAPP_COMMUNICATION,
  VIEW_WHATSAPP_COMMUNICATION_HISTORY
} from '../../Utility/PermissionKeys';
import { parseAndValidatePhoneNumbers } from '../../../helpers/whatsappUtils';
import ViewWhatsAppModal from './ViewWhatsAppModal';
import ViewInboundModal from './ViewInboundModal';
import MessageHistoryTab from './MessageHistoryTab';
import WhatsAppMessageCell from './WhatsAppMessageCell';

const TAB_SEND = 'send';
const TAB_HISTORY = 'history';

const WhatsAppCommunication = ({ authUser }) => {
  const [breadcrumbItems] = useState([
    { title: 'Communication', link: '#' },
    { title: 'Via WhatsApp', link: '#' }
  ]);

  const [activeTab, setActiveTab] = useState(TAB_SEND);

  // ── Send Message form state ──────────────────────────────────────────────
  const [recipientType, setRecipientType] = useState('child');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [messageType, setMessageType] = useState('text');
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [templateOptionsLoading, setTemplateOptionsLoading] = useState(false);
  const [childOptions, setChildOptions] = useState([]);
  const [childOptionsLoading, setChildOptionsLoading] = useState(false);
  const [batchOptions, setBatchOptions] = useState([]);
  const [batchOptionsLoading, setBatchOptionsLoading] = useState(false);
  const [offeringOptions, setOfferingOptions] = useState([]);
  const [offeringOptionsLoading, setOfferingOptionsLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [phoneNumbersInput, setPhoneNumbersInput] = useState('');
  const [purpose, setPurpose] = useState('');

  // ── Add Template form state ──────────────────────────────────────────────
  const [templateNameInput, setTemplateNameInput] = useState('');
  const [creatingTemplate, setCreatingTemplate] = useState(false);

  // ── Sending History table state ──────────────────────────────────────────
  const [historyPagination, setHistoryPagination] = useState({
    currentPage: 1,
    pageLimit: 5,
    totalEntries: 0,
    totalPages: 0
  });
  const [historyLoading, setHistoryLoading] = useState(true);
  const [communicationList, setCommunicationList] = useState([]);
  const [viewModal, setViewModal] = useState({ open: false, comm: null });
  const [viewInboundModal, setViewInboundModal] = useState({ open: false, row: null });
  const [historyFilters, setHistoryFilters] = useState({
    recipientType: '',
    messageType: '',
    templateName: '',
    purpose: '',
    startTime: '',
    endTime: ''
  });
  const [historyPurposeInput, setHistoryPurposeInput] = useState('');
  const [commStats, setCommStats] = useState({ today: 0, thisWeek: 0, thisMonth: 0 });
  const [sortingRule, setSortingRule] = useState('createdAt$desc');
  const [refresh, setRefresh] = useState(false);

  const handleRefresh = () => setRefresh((prev) => !prev);

  // ── Data fetchers ────────────────────────────────────────────────────────
  function buildChildOptions(list) {
    return list.map((item) => ({ value: item._id, label: item.name }));
  }

  function buildBatchOptions(list) {
    return list.map((item) => ({ value: item._id, label: item.name }));
  }

  function buildOfferingOptions(list) {
    return list.map((item) => ({ value: item._id, label: item.name }));
  }

  const fetchChildOptions = async (page, limit, searchParam) => {
    setChildOptionsLoading(true);
    const response = await getChildList(page, limit, searchParam && { filter_name: searchParam });
    if (response) setChildOptions(buildChildOptions(response?.data || []));
    setChildOptionsLoading(false);
  };

  const fetchBatchOptions = async () => {
    setBatchOptionsLoading(true);
    const response = await getBatchList(1, 100);
    if (response) setBatchOptions(buildBatchOptions(response?.data || []));
    setBatchOptionsLoading(false);
  };

  const fetchOfferingOptions = async () => {
    setOfferingOptionsLoading(true);
    const response = await getOfferingList(1, 100);
    if (response) setOfferingOptions(buildOfferingOptions(response?.data || []));
    setOfferingOptionsLoading(false);
  };

  const fetchTemplateOptions = async () => {
    setTemplateOptionsLoading(true);
    try {
      const templates = await getWhatsAppTemplates();
      setTemplateOptions((templates || []).map((t) => ({ value: t._id, label: t.name })));
    } catch {
      toast.error('Failed to load templates');
      setTemplateOptions([]);
    } finally {
      setTemplateOptionsLoading(false);
    }
  };

  const searchChild = useCallback(
    debounce((event) => { fetchChildOptions(1, 50, event); }, 300),
    []
  );

  const debouncedHistoryPurposeSearch = useCallback(
    debounce((value) => {
      setHistoryFilters((prev) => ({ ...prev, purpose: value }));
    }, 400),
    []
  );

  const handleHistoryPurposeChange = (e) => {
    setHistoryPurposeInput(e.target.value);
    debouncedHistoryPurposeSearch(e.target.value);
  };

  const handleFetchSendingHistory = async (page, limit, filters = {}, sort_option) => {
    setHistoryLoading(true);
    try {
      const params = {
        page,
        limit,
        recipientType: filters?.recipientType || '',
        messageType: filters?.messageType || '',
        templateName: filters?.templateName || '',
        purpose: filters?.purpose || '',
        startTime: filters?.startTime || '',
        endTime: filters?.endTime || '',
        sort_option
      };
      const response = await fetchWhatsAppCommunications(params);
      const data = (response?.data || []).map((comm, idx) => {
        const phones = comm.phoneNumbers || [];
        const names = comm.recipientNames || [];
        let mobileNumber = '—';
        if (comm.recipientType === 'phone') {
          mobileNumber = phones.length === 1 ? phones[0] : `${phones.length} numbers`;
        } else if (names.length > 0) {
          mobileNumber = names.length <= 2
            ? names.join(', ')
            : `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
        } else if (comm.recipientType === 'child') {
          mobileNumber = 'Child recipients';
        } else if (comm.recipientType === 'batch') {
          mobileNumber = 'Batch recipients';
        } else if (comm.recipientType === 'offering') {
          mobileNumber = 'Offering recipients';
        }
        return {
          _id: comm._id,
          rowType: 'outbound',
          srNo: (page - 1) * limit + idx + 1,
          sentAt: comm.createdAt ? moment(comm.createdAt).format('DD MMM YYYY HH:mm') : 'N/A',
          statusUpdatedAt: '—',
          mobileNumber,
          purpose: comm.purpose || '',
          message: comm.message,
          status: comm.status,
          direction: 'outbound',
          phoneNumbers: phones,
          _sortKey: comm.createdAt ? new Date(comm.createdAt).getTime() : 0
        };
      });
      setCommunicationList(data);
      setHistoryPagination({
        currentPage: response?.metadata?.page || 1,
        pageLimit: response?.metadata?.limit || limit,
        totalPages: response?.metadata?.total_pages || 0,
        totalEntries: response?.metadata?.total_results || 0
      });
    } catch {
      toast.error('Failed to fetch communications');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleFetchStats = async () => {
    try {
      const stats = await fetchWhatsAppStats();
      if (stats) setCommStats(stats);
    } catch {
      // non-critical
    }
  };

  // ── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => {
    setSelectedRecipients([]);
    setChildOptions([]);
    setBatchOptions([]);
    setOfferingOptions([]);
    setPhoneNumbersInput('');
  }, [recipientType]);

  useEffect(() => {
    setMessage('');
    setSelectedTemplate(null);
  }, [messageType]);

  useEffect(() => {
    if (authUser && recipientType === 'child') fetchChildOptions();
    if (authUser && recipientType === 'batch') fetchBatchOptions();
    if (authUser && recipientType === 'offering') fetchOfferingOptions();
  }, [authUser, recipientType]);

  useEffect(() => {
    if (authUser && messageType === 'template' && validatePermissionKey(authUser, SEND_WHATSAPP_MESSAGE)) {
      fetchTemplateOptions();
    }
  }, [authUser, messageType]);

  useEffect(() => {
    if (authUser && validatePermissionKey(authUser, VIEW_WHATSAPP_COMMUNICATION_HISTORY)) {
      handleFetchSendingHistory(historyPagination.currentPage, historyPagination.pageLimit, historyFilters, sortingRule);
      handleFetchStats();
    } else if (authUser) {
      setHistoryLoading(false);
    }
  }, [authUser, historyFilters, sortingRule, refresh]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!validatePermissionKey(authUser, SEND_WHATSAPP_MESSAGE)) {
      toast.error('You do not have permission to send WhatsApp messages');
      return;
    }

    if (recipientType === 'phone') {
      const { valid, invalid } = parseAndValidatePhoneNumbers(phoneNumbersInput);
      if (invalid.length > 0) {
        toast.info(`Invalid phone number(s): ${invalid.join(', ')}. Please enter valid Indian mobile numbers.`);
        return;
      }
      if (valid.length === 0) {
        toast.info('Please enter at least one valid Indian mobile number (comma-separated) before sending.');
        return;
      }
    } else {
      const ids = Array.isArray(selectedRecipients)
        ? selectedRecipients.map((r) => (typeof r === 'object' ? r.value : r))
        : selectedRecipients?.value ? [selectedRecipients.value] : [];
      if (ids.length === 0) {
        const recipientLabel =
          recipientType === 'child' ? 'child' : recipientType === 'batch' ? 'batch' : 'offering';
        toast.info(`Please select at least one ${recipientLabel} before sending.`);
        return;
      }
    }

    if (messageType === 'text') {
      if (!message || !message.trim()) {
        toast.info('Please enter a message before sending.');
        return;
      }
    } else {
      if (!selectedTemplate?.value && !selectedTemplate) {
        toast.info('Please select a message template before sending.');
        return;
      }
    }

    const purposeTrimmed = purpose.trim() || undefined;
    let payload;
    if (recipientType === 'phone') {
      const { valid } = parseAndValidatePhoneNumbers(phoneNumbersInput);
      payload = {
        recipientType: 'phone',
        phoneNumbers: valid,
        messageType,
        ...(messageType === 'text' ? { message: message.trim() } : { templateId: selectedTemplate?.value || selectedTemplate }),
        ...(purposeTrimmed ? { purpose: purposeTrimmed } : {})
      };
    } else {
      const ids = Array.isArray(selectedRecipients)
        ? selectedRecipients.map((r) => (typeof r === 'object' ? r.value : r))
        : selectedRecipients?.value ? [selectedRecipients.value] : [];
      payload = {
        recipientType,
        recipientIds: ids,
        messageType,
        ...(messageType === 'text' ? { message: message.trim() } : { templateId: selectedTemplate?.value || selectedTemplate }),
        ...(purposeTrimmed ? { purpose: purposeTrimmed } : {})
      };
    }

    setSending(true);
    try {
      const result = await sendWhatsAppMessage(payload);
      const resultStatus = result?.status;
      if (resultStatus === 'failed') {
        toast.error('Message could not be sent — no valid phone numbers found for the selected recipients');
      } else if (resultStatus === 'partial') {
        const failedCount = result?.failedCount || 0;
        toast.warn(`Message partially sent — ${failedCount} recipient(s) could not be reached (missing valid phone numbers). Check Message History for details.`);
      } else {
        toast.success('Message sent successfully');
      }
      setMessage('');
      setSelectedRecipients([]);
      setSelectedTemplate(null);
      setPhoneNumbersInput('');
      setPurpose('');
      handleRefresh();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to send message';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const handleCreateTemplate = async () => {
    const name = templateNameInput?.trim();
    if (!name) {
      toast.info('Please enter a template name (Meta template name from developers.facebook.com).');
      return;
    }
    setCreatingTemplate(true);
    try {
      await createWhatsAppTemplate({ name });
      toast.success('Template added successfully');
      setTemplateNameInput('');
      fetchTemplateOptions();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to add template';
      toast.error(msg);
    } finally {
      setCreatingTemplate(false);
    }
  };

  const handleViewDetail = async (comm) => {
    try {
      const full = await getWhatsAppCommunicationById(comm._id);
      setViewModal({ open: true, comm: full });
      // Sync the outer table row status if the backend self-healed it
      if (full && full.status !== comm.status) {
        setCommunicationList((prev) =>
          prev.map((r) => (r._id === comm._id ? { ...r, status: full.status } : r))
        );
      }
    } catch {
      toast.error('Failed to load details');
    }
  };

  const handleRefreshModal = async () => {
    const commId = viewModal.comm?._id;
    if (!commId) return;
    try {
      const full = await getWhatsAppCommunicationById(commId);
      setViewModal((prev) => ({ ...prev, comm: full }));
      setCommunicationList((prev) =>
        prev.map((r) => (r._id === commId ? { ...r, status: full.status } : r))
      );
    } catch {
      toast.error('Failed to refresh details');
    }
  };

  // ── Permission guards ────────────────────────────────────────────────────
  if (authUser && authUser.user_type === 'staff' && !validatePermissionKey(authUser, VIEW_WHATSAPP_COMMUNICATION)) {
    return <Redirect to="/dashboard" />;
  }

  const hasSendPermission = validatePermissionKey(authUser, SEND_WHATSAPP_MESSAGE);
  const hasHistoryPermission = validatePermissionKey(authUser, VIEW_WHATSAPP_COMMUNICATION_HISTORY);

  const historyTableColumns = [
    { label: 'Message Sent', key: 'sentAt' },
    { label: 'Status Updated', key: 'statusUpdatedAt' },
    { label: 'Mobile Number', key: 'mobileNumber' },
    { label: 'Purpose', key: 'purpose', render: (row) => row.purpose || '—' },
    {
      label: 'Message',
      key: 'message',
      render: (row) => (
        <WhatsAppMessageCell
          message={row.message}
          rowId={row._id}
          context={{
            phone: row.mobileNumber,
            purpose: row.purpose,
            sentAt: row.sentAt
          }}
        />
      )
    },
    {
      label: 'Status',
      key: 'status',
      render: (row) => {
        const colorMap = {
          'in progress': 'secondary',
          sent: 'warning',
          delivered: 'primary',
          read: 'info',
          received: 'success',
          failed: 'danger',
          partial: 'danger'
        };
        return <Badge color={colorMap[row.status] || 'secondary'}>{row.status}</Badge>;
      }
    },
    {
      label: 'Mode',
      key: 'direction',
      render: (row) =>
        row.direction === 'inbound' ? (
          <Badge color="success">Message Received</Badge>
        ) : (
          <Badge color="primary">Message Sent</Badge>
        )
    },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Via WhatsApp" breadcrumbItems={breadcrumbItems} />

        {/* Inner tabs */}
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === TAB_SEND })}
              onClick={() => setActiveTab(TAB_SEND)}
              style={{ cursor: 'pointer' }}
            >
              Send Message
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === TAB_HISTORY })}
              onClick={() => setActiveTab(TAB_HISTORY)}
              style={{ cursor: 'pointer' }}
            >
              Message History
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>

          {/* ── Tab 1: Send Message ── */}
          <TabPane tabId={TAB_SEND}>

            {/* Compose card */}
            {hasSendPermission && (
              <Row>
                <Col>
                  <Card>
                    <CardBody>
                      <h5 className="mb-3">Send WhatsApp Message</h5>
                      <Row>
                        <Col md="3">
                          <FormGroup>
                            <Label>Recipient Type</Label>
                            <Input
                              type="select"
                              value={recipientType}
                              onChange={(e) => setRecipientType(e.target.value)}
                              disabled={
                                recipientType === 'child'
                                  ? childOptionsLoading
                                  : recipientType === 'batch'
                                    ? batchOptionsLoading
                                    : recipientType === 'offering'
                                      ? offeringOptionsLoading
                                      : false
                              }
                            >
                              <option value="child">Child (child + mother & father)</option>
                              <option value="batch">Batch (child + mother & father)</option>
                              <option value="offering">Offering (batches → child + mother & father)</option>
                              <option value="phone">Phone number</option>
                            </Input>
                          </FormGroup>
                        </Col>
                        <Col md="5">
                          <FormGroup>
                            <Label>
                              {recipientType === 'phone'
                                ? 'Phone number(s)'
                                : `Select ${
                                    recipientType === 'child'
                                      ? 'Child'
                                      : recipientType === 'batch'
                                        ? 'Batch'
                                        : 'Offering'
                                  }`}
                            </Label>
                            {recipientType === 'phone' ? (
                              <Input
                                type="textarea"
                                rows={2}
                                value={phoneNumbersInput}
                                onChange={(e) => setPhoneNumbersInput(e.target.value)}
                                placeholder="Enter comma-separated Indian mobile numbers (e.g. 9876543210, 9123456789)"
                                disabled={sending}
                              />
                            ) : recipientType === 'child' ? (
                              <Select
                                isMulti
                                options={childOptions}
                                value={selectedRecipients}
                                onChange={(val) => setSelectedRecipients(val)}
                                onInputChange={searchChild}
                                isLoading={childOptionsLoading}
                                placeholder="Type to search child(ren)..."
                              />
                            ) : recipientType === 'batch' ? (
                              <Select
                                isMulti
                                options={batchOptions}
                                value={selectedRecipients}
                                onChange={(val) => setSelectedRecipients(val)}
                                isLoading={batchOptionsLoading}
                                placeholder="Select batch(es)..."
                              />
                            ) : (
                              <Select
                                isMulti
                                options={offeringOptions}
                                value={selectedRecipients}
                                onChange={(val) => setSelectedRecipients(val)}
                                isLoading={offeringOptionsLoading}
                                placeholder="Select offering(s)..."
                              />
                            )}
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="6">
                          <FormGroup>
                            <Label>Message Type</Label>
                            <Input type="select" value={messageType} onChange={(e) => setMessageType(e.target.value)} disabled={sending}>
                              <option value="text">Text</option>
                              <option value="template">Template</option>
                            </Input>
                          </FormGroup>
                        </Col>
                        <Col md="6">
                          <FormGroup>
                            <Label>Purpose <span className="text-muted">(optional)</span></Label>
                            <Input
                              type="text"
                              value={purpose}
                              onChange={(e) => setPurpose(e.target.value)}
                              placeholder="e.g. Fee reminder - April, Batch schedule update"
                              disabled={sending}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="12">
                          <FormGroup>
                            <Label>{messageType === 'text' ? 'Message' : 'Message Template'}</Label>
                            {messageType === 'text' ? (
                              <Input
                                type="textarea"
                                rows={3}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Enter your message..."
                                disabled={sending}
                              />
                            ) : (
                              <Select
                                options={templateOptions}
                                value={selectedTemplate}
                                onChange={(val) => setSelectedTemplate(val)}
                                isLoading={templateOptionsLoading}
                                placeholder="Select a template..."
                                isClearable
                              />
                            )}
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Button
                            color="primary"
                            onClick={handleSend}
                            disabled={
                              sending ||
                              (recipientType === 'child' && childOptionsLoading) ||
                              (recipientType === 'batch' && batchOptionsLoading) ||
                              (recipientType === 'offering' && offeringOptionsLoading) ||
                              (messageType === 'template' && templateOptionsLoading)
                            }
                          >
                            {sending ? 'Sending...' : 'Send via WhatsApp'}
                          </Button>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Sending history */}
            {hasHistoryPermission && (
              <Row>
                <Col>
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">Message History</h5>
                        <div className="d-flex align-items-center" style={{ gap: '1rem' }}>
                          <span className="text-muted small">
                            Today: <strong>{commStats.today}</strong>
                            &nbsp;|&nbsp;This Week: <strong>{commStats.thisWeek}</strong>
                            &nbsp;|&nbsp;This Month: <strong>{commStats.thisMonth}</strong>
                          </span>
                          <Button color="outline-secondary" size="sm" onClick={handleRefresh} disabled={historyLoading}>
                            <i className="fas fa-sync-alt mr-1"></i> Refresh
                          </Button>
                        </div>
                      </div>
                      <Row className="mt-2">
                        <Col md="2">
                          <FormGroup>
                            <Label>Show Entries</Label>
                            <Input
                              type="select"
                              value={historyPagination.pageLimit}
                              onChange={(e) => handleFetchSendingHistory(1, Number(e.target.value), historyFilters, sortingRule)}
                              disabled={historyLoading}
                            >
                              <option value={5}>5</option>
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </Input>
                          </FormGroup>
                        </Col>
                        <Col md="2">
                          <FormGroup>
                            <Label>Message Type</Label>
                            <Input
                              type="select"
                              value={historyFilters.messageType}
                              onChange={(e) => {
                                const updated = { ...historyFilters, messageType: e.target.value, templateName: '' };
                                setHistoryFilters(updated);
                                handleFetchSendingHistory(1, historyPagination.pageLimit, updated, sortingRule);
                              }}
                              disabled={historyLoading}
                            >
                              <option value="">All Types</option>
                              <option value="template">Template</option>
                              <option value="text">Text</option>
                            </Input>
                          </FormGroup>
                        </Col>
                        {historyFilters.messageType === 'template' && (
                          <Col md="3">
                            <FormGroup>
                              <Label>Template Name</Label>
                              <Input
                                type="text"
                                value={historyFilters.templateName}
                                onChange={(e) => {
                                  const updated = { ...historyFilters, templateName: e.target.value };
                                  setHistoryFilters(updated);
                                }}
                                onBlur={(e) => {
                                  handleFetchSendingHistory(1, historyPagination.pageLimit, historyFilters, sortingRule);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleFetchSendingHistory(1, historyPagination.pageLimit, historyFilters, sortingRule);
                                }}
                                placeholder="e.g. mothers_day"
                                disabled={historyLoading}
                              />
                            </FormGroup>
                          </Col>
                        )}
                        <Col md="3">
                          <FormGroup>
                            <Label>Purpose</Label>
                            <Input
                              type="text"
                              value={historyPurposeInput}
                              onChange={handleHistoryPurposeChange}
                              placeholder="e.g. Fee reminder"
                              disabled={historyLoading}
                              data-testid="history-purpose-filter"
                            />
                          </FormGroup>
                        </Col>
                        <Col md="3">
                          <FormGroup>
                            <Label>Sort</Label>
                            <Input
                              type="select"
                              value={sortingRule}
                              onChange={(e) => setSortingRule(e.target.value)}
                              disabled={historyLoading}
                            >
                              <option value="createdAt$desc">Newest First</option>
                              <option value="createdAt$asc">Oldest First</option>
                            </Input>
                          </FormGroup>
                        </Col>
                      </Row>

                      {historyLoading ? (
                        <div className="text-center">
                          <PageDataLoader height={40} width={40} />
                        </div>
                      ) : (
                        <div className="table-rep-plugin">
                          <div className="table-responsive mb-0" data-pattern="priority-columns">
                            <Table id="whatsapp-history" striped bordered responsive>
                              <thead>
                                <tr>
                                  {historyTableColumns.map((col, i) => <th key={i}>{col.label}</th>)}
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {communicationList.length === 0 ? (
                                  <tr>
                                    <td colSpan={historyTableColumns.length + 1} className="text-center">
                                      No communications yet
                                    </td>
                                  </tr>
                                ) : (
                                  communicationList.map((row, i) => (
                                    <tr key={row._id || i}>
                                      {historyTableColumns.map((col, idx) => (
                                        <td key={idx}>{col?.render ? col.render(row) : row[col?.key]}</td>
                                      ))}
                                      <td>
                                        {row.rowType === 'inbound' ? (
                                          <Button size="sm" color="success" onClick={() => setViewInboundModal({ open: true, row })}>
                                            <i className="fas fa-eye mr-2"></i>View
                                          </Button>
                                        ) : (
                                          <Button size="sm" color="primary" onClick={() => handleViewDetail(row)}>
                                            <i className="fas fa-eye mr-2"></i>View
                                          </Button>
                                        )}
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </Table>
                          </div>
                        </div>
                      )}

                      <Row className="mt-2">
                        <Col md="5">
                          <p>
                            <strong>
                              Showing {(historyPagination.currentPage - 1) * historyPagination.pageLimit + 1} to{' '}
                              {Math.min(historyPagination.currentPage * historyPagination.pageLimit, historyPagination.totalEntries)} of{' '}
                              {historyPagination.totalEntries} entries
                            </strong>
                          </p>
                        </Col>
                        {!historyLoading && communicationList.length > 0 && (
                          <Col md="7">
                            <Paginator
                              totalPages={historyPagination.totalPages}
                              currentPage={historyPagination.currentPage}
                              changePage={(page) => handleFetchSendingHistory(page, historyPagination.pageLimit, historyFilters, sortingRule)}
                            />
                          </Col>
                        )}
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Add template */}
            {hasSendPermission && (
              <Row>
                <Col>
                  <Card>
                    <CardBody>
                      <h5 className="mb-3">Add Message Template</h5>
                      <p className="text-muted small mb-2">
                        Create templates in{' '}
                        <a href="https://developers.facebook.com/" target="_blank" rel="noopener noreferrer">
                          Meta for Developers
                        </a>
                        , then add the template name here (e.g. hello_world). Default language: en.
                      </p>
                      <Row>
                        <Col md="4">
                          <FormGroup>
                            <Label>Template Name</Label>
                            <Input
                              type="text"
                              value={templateNameInput}
                              onChange={(e) => setTemplateNameInput(e.target.value)}
                              placeholder="e.g. hello_world"
                              disabled={creatingTemplate}
                            />
                          </FormGroup>
                        </Col>
                        <Col md="2" className="d-flex align-items-end">
                          <FormGroup>
                            <Button
                              color="primary"
                              onClick={handleCreateTemplate}
                              disabled={creatingTemplate || !templateNameInput?.trim()}
                            >
                              {creatingTemplate ? 'Adding...' : 'Add Template'}
                            </Button>
                          </FormGroup>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}
          </TabPane>

          {/* ── Tab 2: Message History ── */}
          <TabPane tabId={TAB_HISTORY}>
            {hasHistoryPermission ? (
              <MessageHistoryTab />
            ) : (
              <p className="text-muted">You do not have permission to view message history.</p>
            )}
          </TabPane>

        </TabContent>

        {viewModal.open && (
          <ViewWhatsAppModal
            isOpen={viewModal.open}
            handleClose={() => setViewModal({ open: false, comm: null })}
            commData={viewModal.comm}
            onRefresh={handleRefreshModal}
          />
        )}

        {viewInboundModal.open && (
          <ViewInboundModal
            isOpen={viewInboundModal.open}
            handleClose={() => setViewInboundModal({ open: false, row: null })}
            row={viewInboundModal.row}
          />
        )}
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => ({
  authUser: state.Login?.user
});

export default connect(mapStateToProps)(WhatsAppCommunication);
